import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import "maplibre-gl/dist/maplibre-gl.css";
import "./traccar-map.css";
import maplibregl from "maplibre-gl";
import useMapStyles from "./useMapStyles";

function buildGeoJSON(positions = [], devices = []) {
  const latest = new Map();
  const deviceMap = new Map(devices.map((d) => [d.id, d]));

  for (const p of positions) {
    const lat = Number(p?.latitude);
    const lon = Number(p?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    const prev = latest.get(p.deviceId);
    const tPrev = prev
      ? new Date(prev.fixTime || prev.deviceTime || prev.serverTime || 0).getTime()
      : -1;
    const tNow = new Date(
      p.fixTime || p.deviceTime || p.serverTime || 0
    ).getTime();

    if (!prev || tNow >= tPrev) {
      const device = deviceMap.get(p.deviceId);
      let serviceCode = "N/A";
      try {
        const serviceInfo = p.attributes?.service_info;
        if (serviceInfo) {
          const parsed =
            typeof serviceInfo === "string"
              ? JSON.parse(serviceInfo)
              : serviceInfo;
          serviceCode = parsed?.service_code || "N/A";
        }
      } catch (_) {}
      latest.set(p.deviceId, {
        ...p,
        deviceName: device?.name || `Device ${p.deviceId}`,
        serviceCode,
      });
    }
  }

  return {
    type: "FeatureCollection",
    features: Array.from(latest.values()).map((p) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [Number(p.longitude), Number(p.latitude)],
      },
      properties: {
        deviceId: p.deviceId,
        deviceName: p.deviceName,
        serviceCode: p.serviceCode,
      },
    })),
  };
}

function buildRoutesGeoJSON(positionHistory = {}) {
  const features = [];
  Object.entries(positionHistory).forEach(([deviceId, points]) => {
    if (!points || points.length < 2) return;

    const coordinates = points
      .filter((p) => p.lat && p.lng)
      .map((p) => [Number(p.lng), Number(p.lat)]);

    if (coordinates.length < 2) return;
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates },
      properties: { deviceId: Number(deviceId) },
    });
  });

  return { type: "FeatureCollection", features };
}

function loadSvgImage(map, name, svg, w, h) {
  return new Promise((resolve) => {
    if (map.hasImage(name)) {
      resolve();
      return;
    }

    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const img = new Image(w, h);

    img.onload = () => {
      try {
        if (!map.hasImage(name)) map.addImage(name, img);
      } catch (_) {}
      URL.revokeObjectURL(url);
      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    img.src = url;
  });
}

function preloadPins(map) {
  const svgGray = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <circle cx="14" cy="14" r="12" fill="#757575" stroke="white" stroke-width="2.5"/>
    <circle cx="14" cy="14" r="4.5" fill="white"/>
  </svg>`;

  const svgBlue = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="14" fill="#1976d2" stroke="white" stroke-width="3"/>
    <circle cx="17" cy="17" r="5.5" fill="white"/>
  </svg>`;

  return Promise.all([
    loadSvgImage(map, "pin-gray", svgGray, 28, 28),
    loadSvgImage(map, "pin-selected", svgBlue, 34, 34),
  ]);
}

class StyleSwitcherControl {
  constructor(styles, onStyleChange) {
    this.styles = styles;
    this.onStyleChange = onStyleChange;
    this.currentStyle = styles[0]?.id;
  }

  onAdd(map) {
    this.map = map;
    this.container = document.createElement("div");
    this.container.className = "maplibregl-ctrl maplibregl-ctrl-group";

    this.button = document.createElement("button");
    this.button.className = "maplibre-style-switcher";
    this.button.innerHTML = '<span class="layer-icon"></span>';
    this.button.title = "Cambiar estilo del mapa";

    this.dropdown = document.createElement("div");
    this.dropdown.className = "maplibre-style-list";

    this.styles.forEach((style) => {
      const option = document.createElement("div");
      option.className = `style-option ${
        style.id === this.currentStyle ? "active" : ""
      }`;
      option.textContent = style.name;
      option.dataset.styleId = style.id;

      option.addEventListener("click", (e) => {
        e.stopPropagation();
        this.selectStyle(style.id);
        this.hideDropdown();
      });

      this.dropdown.appendChild(option);
    });

    this.button.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleDropdown();
    });

    this._clickHandler = () => this.hideDropdown();
    this._keydownHandler = (e) => {
      if (e.key === "Escape") this.hideDropdown();
    };

    document.addEventListener("click", this._clickHandler);
    document.addEventListener("keydown", this._keydownHandler);

    this.container.appendChild(this.button);
    this.container.appendChild(this.dropdown);

    return this.container;
  }

  onRemove() {
    document.removeEventListener("keydown", this._keydownHandler);
    document.removeEventListener("click", this._clickHandler);
    this.container?.parentNode?.removeChild(this.container);
    this.map = undefined;
  }

  toggleDropdown() {
    this.dropdown.classList.toggle("show");
  }

  hideDropdown() {
    this.dropdown.classList.remove("show");
  }

  selectStyle(styleId) {
    if (styleId === this.currentStyle) return;
    this.currentStyle = styleId;

    this.dropdown.querySelectorAll(".style-option").forEach((o) =>
      o.classList.toggle("active", o.dataset.styleId === styleId)
    );

    this.onStyleChange(styleId);
  }
}

function throttle(fn, ms) {
  let lastCall = 0;
  let rafId = null;

  return (...args) => {
    const now = Date.now();
    if (now - lastCall < ms) return;
    lastCall = now;
    if (rafId) cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      fn(...args);
      rafId = null;
    });
  };
}

const PIN_LAYERS = [
  "clusters",
  "cluster-count",
  "unclustered-pin",
  "selected-pin",
  "pin-label-name",
  "pin-label-service",
];

const ROUTE_LAYERS = ["route-line-border", "route-line"];
const OUR_LAYERS = [...ROUTE_LAYERS, ...PIN_LAYERS];
const OUR_SOURCES = ["routes-src", "devices-src"];

const ROUTES_MIN_ZOOM = 11;
const LABELS_MIN_ZOOM = 14;

const MapView = ({ positions = [], devices = [], selectedDeviceId, onDeviceSelect, positionHistory = {} }) => {

  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const popupUserClosedRef = useRef(false);
  const fittedOnceRef = useRef(false);
  const mapReadyRef = useRef(false);

  const devicesRef = useRef(devices);
  const positionsRef = useRef(positions);
  const selectedDeviceIdRef = useRef(selectedDeviceId);
  const dataRef = useRef(null);
  const positionHistoryRef = useRef(positionHistory);
  const updateRouteSourceRef = useRef(null);
  const pendingPinsRef = useRef(false);

  const styles = useMapStyles();
  const stylesRef = useRef(styles);

  useEffect(() => {
    stylesRef.current = styles;
  }, [styles]);

  const [currentStyleId, setCurrentStyleId] = useState(styles[0]?.id);
  const currentStyle = styles.find((s) => s.id === currentStyleId);
  const styleUrl = currentStyle?.url;

  const data = useMemo(
    () => buildGeoJSON(positions, devices),
    [positions, devices]
  );

  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    selectedDeviceIdRef.current = selectedDeviceId;
  }, [selectedDeviceId]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    positionHistoryRef.current = positionHistory;
  }, [positionHistory]);

  const createPopupHTML = useCallback((deviceId, position) => {
    const device = devicesRef.current.find((d) => d.id === deviceId);
    const devName = device?.name || `Device ${deviceId}`;

    let serviceCode = "N/A";
    let plate = "N/A";
    let model = "N/A";
    let brand = "N/A";
    let subBrand = "N/A";

    try {
      const si = position?.attributes?.service_info;
      if (si) {
        const p = typeof si === "string" ? JSON.parse(si) : si;
        serviceCode = p?.service_code || "N/A";
      }
    } catch (_) {}

    try {
      const vi = position?.attributes?.vehicle_info;
      if (vi) {
        const p = typeof vi === "string" ? JSON.parse(vi) : vi;
        plate = p?.plate || "N/A";
        model = p?.model || "N/A";
        brand = p?.brand || "N/A";
        subBrand = p?.sub_brand || "N/A";
      }
    } catch (_) {}

    const address = position?.address || "";
    const speed =
      position?.speed !== undefined
        ? `${(Number(position.speed) * 1.852).toFixed(2)} km/h`
        : "0.00 km/h";

    return `<div class="dp-container"><div class="dp-header"><p class="dp-name">${devName}</p></div><div class="dp-body">
      <div class="dp-row"><span class="dp-label">Servicio</span><span class="dp-value">${serviceCode}</span></div>
      <div class="dp-row"><span class="dp-label">Placa</span><span class="dp-value">${plate}</span></div>
      <div class="dp-row"><span class="dp-label">Modelo</span><span class="dp-value">${model}</span></div>
      <div class="dp-row"><span class="dp-label">Marca</span><span class="dp-value">${brand}</span></div>
      <div class="dp-row"><span class="dp-label">Submarca</span><span class="dp-value">${subBrand}</span></div>
      ${
        address
          ? `<div class="dp-divider"></div><div class="dp-row dp-row-full"><span class="dp-label">Dirección Calle</span><span class="dp-value">${address}</span></div>`
          : ""
      }
      <div class="dp-divider"></div>
      <div class="dp-row"><span class="dp-label">Velocidad</span><span class="dp-value">${speed}</span></div>
    </div></div>`;
  }, []);

  const addOurLayers = useCallback(
    (map) => {
      const currentData = dataRef.current || {
        type: "FeatureCollection",
        features: [],
      };
      const currentSelectedId = selectedDeviceIdRef.current;
      const currentHistory = positionHistoryRef.current;

      OUR_LAYERS.forEach((id) => {
        try {
          if (map.getLayer(id)) map.removeLayer(id);
        } catch (_) {}
      });

      OUR_SOURCES.forEach((id) => {
        try {
          if (map.getSource(id)) map.removeSource(id);
        } catch (_) {}
      });

      const addL = (layerDef) => {
        try {
          map.addLayer(layerDef);
        } catch (e) {
          console.warn(
            `[MapView] addLayer failed — id="${layerDef.id}" type="${layerDef.type}":`,
            e.message
          );
        }
      };

      const bringToTop = () => {
        OUR_LAYERS.forEach((id) => {
          try {
            if (map.getLayer(id)) map.moveLayer(id);
          } catch (_) {}
        });
      };

      map.addSource("routes-src", {
        type: "geojson",
        data: buildRoutesGeoJSON(currentHistory),
        buffer: 0,
        tolerance: 2,
      });

      addL({
        id: "route-line-border",
        type: "line",
        source: "routes-src",
        minzoom: ROUTES_MIN_ZOOM,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#ffffff",
          "line-width": 6,
          "line-opacity": 0.6,
        },
      });

      addL({
        id: "route-line",
        type: "line",
        source: "routes-src",
        minzoom: ROUTES_MIN_ZOOM,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#1976d2",
          "line-width": 3.5,
          "line-opacity": 0.9,
        },
      });

      map.addSource("devices-src", {
        type: "geojson",
        data: currentData,
        cluster: true,
        clusterRadius: 60,
        clusterMaxZoom: 13,
        buffer: 0,
        tolerance: 0.5,
        generateId: true,
      });

      addL({
        id: "clusters",
        type: "circle",
        source: "devices-src",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#ffffff",
          "circle-stroke-color": "#bdbdbd",
          "circle-stroke-width": 1,
          "circle-radius": ["step", ["get", "point_count"], 14, 10, 18, 50, 24],
        },
      });

      addL({
        id: "cluster-count",
        type: "symbol",
        source: "devices-src",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-size": 12,
        },
        paint: { "text-color": "#333" },
      });

      addL({
        id: "unclustered-pin",
        type: "symbol",
        source: "devices-src",
        filter: [
          "all",
          ["!", ["has", "point_count"]],
          ["!=", ["get", "deviceId"], currentSelectedId || -1],
        ],
        layout: {
          "icon-image": "pin-gray",
          "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.6, 10, 0.85, 14, 1.0],
          "icon-allow-overlap": true,
          "icon-anchor": "center",
          "icon-ignore-placement": true,
        },
      });

      addL({
        id: "selected-pin",
        type: "symbol",
        source: "devices-src",
        filter: [
          "all",
          ["!", ["has", "point_count"]],
          ["==", ["get", "deviceId"], currentSelectedId || -1],
        ],
        layout: {
          "icon-image": "pin-selected",
          "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.7, 10, 0.95, 14, 1.1],
          "icon-allow-overlap": true,
          "icon-anchor": "center",
          "icon-ignore-placement": true,
        },
      });

      addL({
        id: "pin-label-name",
        type: "symbol",
        source: "devices-src",
        filter: ["!", ["has", "point_count"]],
        minzoom: LABELS_MIN_ZOOM,
        layout: {
          "text-field": ["get", "deviceName"],
          "text-size": 10,
          "text-offset": [0, -5],
          "text-anchor": "bottom",
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": "#212121",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        },
      });

      addL({
        id: "pin-label-service",
        type: "symbol",
        source: "devices-src",
        filter: ["!", ["has", "point_count"]],
        minzoom: LABELS_MIN_ZOOM,
        layout: {
          "text-field": ["get", "serviceCode"],
          "text-size": 9,
          "text-offset": [0, -3.5],
          "text-anchor": "bottom",
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": "#616161",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.2,
        },
      });

      const zoomToCluster = async (e) => {
        const fs = map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
        if (!fs.length) return;

        try {
          const zoom = await map
            .getSource("devices-src")
            .getClusterExpansionZoom(fs[0].properties.cluster_id);

          map.easeTo({
            center: fs[0].geometry.coordinates,
            zoom: zoom + 1,
            duration: 500,
          });
        } catch (_) {}
      };

      const handlePinClick = (e) => {
        const f = e.features?.[0];
        if (!f) return;

        const deviceId = f.properties.deviceId;
        const position = positionsRef.current.find(
          (p) => p.deviceId === deviceId
        );
        const coords = f.geometry.coordinates.slice();

        if (onDeviceSelect) onDeviceSelect(deviceId);

        popupRef.current
          ?.setLngLat(coords)
          .setHTML(createPopupHTML(deviceId, position))
          .addTo(map);

        map.easeTo({
          center: coords,
          zoom: Math.max(map.getZoom(), 15),
          duration: 600,
        });
      };

      map.on("click", "clusters", zoomToCluster);
      map.on("click", "cluster-count", zoomToCluster);
      map.on("click", "unclustered-pin", handlePinClick);
      map.on("click", "selected-pin", handlePinClick);

      PIN_LAYERS.forEach((layer) => {
        map.on("mouseenter", layer, () => (map.getCanvas().style.cursor = "pointer"));
        map.on("mouseleave", layer, () => (map.getCanvas().style.cursor = ""));
      });

      bringToTop();
    },
    [onDeviceSelect, createPopupHTML]
  );

  const styleUrlRef = useRef(styleUrl);

  useEffect(() => {
    styleUrlRef.current = styleUrl;
  }, [styleUrl]);

  useEffect(() => {
    if (!styleUrlRef.current) return;
    if (mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: styleUrlRef.current,
      center: [-102.5528, 23.6345],
      zoom: 5,
      fadeDuration: 0,
      attributionControl: false,
      antialias: false,
    });

    mapRef.current = map;

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    if (styles.length > 1) {
      map.addControl(
        new StyleSwitcherControl(styles, (id) => {
          const style = stylesRef.current.find((s) => s.id === id);
          if (style) applyStyle(map, style.url);
        }),
        "top-right"
      );
    }

    popupRef.current = new maplibregl.Popup({
      offset: 25,
      closeOnClick: false,
      anchor: "top",
      closeButton: true,
    });

    popupRef.current.on("close", () => {
      popupUserClosedRef.current = true;
    });

    map.on("styleimagemissing", (e) => {
      const px = new Image(1, 1);
      px.src =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";

      px.onload = () => {
        try {
          if (!map.hasImage(e.id)) map.addImage(e.id, px);
        } catch (_) {}
      };
    });

    map.on("load", () => {
      mapReadyRef.current = true;
      addOurLayers(map);

      const currentData = dataRef.current;
      if (currentData?.features?.length) {
        const src = map.getSource("devices-src");
        if (src) src.setData(currentData);
      }
      pendingPinsRef.current = false;

      if (!fittedOnceRef.current && dataRef.current?.features?.length) {
        const bbox = new maplibregl.LngLatBounds();
        dataRef.current.features.forEach((f) =>
          bbox.extend(f.geometry.coordinates)
        );
        map.fitBounds(bbox, {
          padding: 60,
          maxZoom: 15,
          duration: 800,
        });
        fittedOnceRef.current = true;
      }

      preloadPins(map).then(() => map.triggerRepaint());

      updateRouteSourceRef.current = throttle(() => {
        const m = mapRef.current;
        if (!m || !mapReadyRef.current) return;
        if (m.getZoom() < ROUTES_MIN_ZOOM) return;

        const src = m.getSource("routes-src");
        if (src)
          src.setData(buildRoutesGeoJSON(positionHistoryRef.current));
      }, 20000);
    });

    return () => {
      mapReadyRef.current = false;
      updateRouteSourceRef.current = null;
      popupRef.current?.remove();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      pendingPinsRef.current = true;
      return;
    }

    const src = map.getSource("devices-src");
    if (src) src.setData(data);

    if (!fittedOnceRef.current && data.features.length) {
      const bbox = new maplibregl.LngLatBounds();
      data.features.forEach((f) => bbox.extend(f.geometry.coordinates));
      map.fitBounds(bbox, {
        padding: 60,
        maxZoom: 15,
        duration: 800,
      });
      fittedOnceRef.current = true;
    }
  }, [data]);

  useEffect(() => {
    updateRouteSourceRef.current?.();
  }, [positionHistory]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) return;
    if (map.getLayer("unclustered-pin"))
      map.setFilter("unclustered-pin", [
        "all",
        ["!", ["has", "point_count"]],
        ["!=", ["get", "deviceId"], selectedDeviceId || -1],
      ]);

    if (map.getLayer("selected-pin"))
      map.setFilter("selected-pin", [
        "all",
        ["!", ["has", "point_count"]],
        ["==", ["get", "deviceId"], selectedDeviceId || -1],
      ]);

    if (!selectedDeviceId) {
      popupRef.current?.remove();
      return;
    }

    popupUserClosedRef.current = false;

    const pos = positionsRef.current.find(
      (p) => p.deviceId === selectedDeviceId
    );
    if (!pos) return;

    const coords = [Number(pos.longitude), Number(pos.latitude)];

    map.easeTo({
      center: coords,
      zoom: Math.max(map.getZoom(), 15),
      duration: 800,
    });

    popupRef.current
      ?.setLngLat(coords)
      .setHTML(createPopupHTML(selectedDeviceId, pos))
      .addTo(map);
  }, [selectedDeviceId, createPopupHTML]);

  useEffect(() => {
    if (!selectedDeviceId || popupUserClosedRef.current) return;
    if (!popupRef.current?.isOpen()) return;

    const pos = positions.find((p) => p.deviceId === selectedDeviceId);
    if (!pos) return;

    const coords = [Number(pos.longitude), Number(pos.latitude)];
    popupRef.current
      .setLngLat(coords)
      .setHTML(createPopupHTML(selectedDeviceId, pos));
  }, [positions, selectedDeviceId, createPopupHTML]);

  const addOurLayersRef = useRef(null);
  addOurLayersRef.current = addOurLayers;

  const applyStylePendingRef = useRef(null);
  const styleLoadHandlerRef = useRef(null);

  const applyStyle = useCallback((map, url) => {
    if (applyStylePendingRef.current)
      clearTimeout(applyStylePendingRef.current);
    applyStylePendingRef.current = setTimeout(() => {
      applyStylePendingRef.current = null;

      const center = map.getCenter();
      const zoom = map.getZoom();
      const bearing = map.getBearing();
      const pitch = map.getPitch();

      mapReadyRef.current = false;
      popupRef.current?.remove();

      if (styleLoadHandlerRef.current) {
        map.off("style.load", styleLoadHandlerRef.current);
        styleLoadHandlerRef.current = null;
      }

      const onLoad = () => {
        styleLoadHandlerRef.current = null
        preloadPins(map).then(() => {
          mapReadyRef.current = true;
          addOurLayersRef.current(map);
          map.jumpTo({ center, zoom, bearing, pitch });

          const src = map.getSource("devices-src");
          if (src && dataRef.current?.features?.length)
            src.setData(dataRef.current);
          map.triggerRepaint();
        });
      };

      styleLoadHandlerRef.current = onLoad;
      map.once("style.load", onLoad);
      map.setStyle(url);
    }, 80);
  }, []);

  return (
    <div
      style={{ width: "100%", height: "100%" }}
      ref={mapContainer}
    />
  );
};

MapView.propTypes = {
  positions: PropTypes.array,
  devices: PropTypes.array,
  selectedDeviceId: PropTypes.number,
  onDeviceSelect: PropTypes.func,
  positionHistory: PropTypes.object,
};

export default MapView;
